import {
  Avatar,
  Box,
  Button,
  Card,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useState } from "react";
import { foyerApi, type AppRecord } from "../http/foyer";
import { friendlyError } from "../utils/apiError";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { useCan } from "../auth/AuthContext";
import AssetField from "../components/AssetField";
import ColorSwatch from "../components/ColorSwatch";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import {
  APP_CERTIFICATIONS,
  CERTIFICATION_LABEL,
  isAppCertification,
} from "@shared/app-certification";
import {
  LAUNCH_PLATFORMS,
  LAUNCH_PLATFORM_LABELS,
  appCoverage,
  resolveLaunchTarget,
  type LaunchPlatform,
} from "@shared/app-resolve";

/**
 * Admin-only global catalogue of Apps. Non-admin users can still GET the
 * list (read-only), but only admins reach this page (nav item is hidden)
 * and the mutation endpoints reject anyone else with 403.
 *
 * Premises consume this catalogue: per-premise selection + overrides are
 * resolved against it in `shared/app-merge.ts`.
 */
/** Short platform tags, for the coverage chips. */
const PLATFORM_TAGS: Record<LaunchPlatform, string> = {
  android: "Android",
  tizen: "Tizen",
  webos: "webOS",
  ios: "iOS",
  web: "Web",
};

/**
 * Which platforms this catalogue entry can open on - the "streaming-app
 * aggregation" view of one row. A filled chip means a native launch id for that
 * OS; an outlined "Web" chip means only the https fallback, which opens a
 * browser rather than the app. A platform with neither is shown greyed so the
 * gap is visible at a glance instead of only surfacing as a dead tile on a TV.
 * The name is part of the input on purpose: in a browser a well-known app opens
 * its own site even with nothing filled in, and the chips must say so.
 */
function CoverageChips({
  app,
}: {
  app: {
    name: string;
    androidSlug: string | null;
    tizenId: string | null;
    webosId: string | null;
    iosScheme: string | null;
    webUrl: string | null;
  };
}) {
  const covered = new Set(appCoverage(app));
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {LAUNCH_PLATFORMS.map((p) => {
        const native = covered.has(p) && p !== "web";
        const target = resolveLaunchTarget(app, p);
        const fallback = !native && target.kind === "web";
        const title = native
          ? `${LAUNCH_PLATFORM_LABELS[p]}: native launch id set`
          : fallback
            ? target.source === "knownApp"
              ? `${LAUNCH_PLATFORM_LABELS[p]}: no address set - a browser opens the known site for "${app.name}" (${target.id})`
              : `${LAUNCH_PLATFORM_LABELS[p]}: opens the web address in a browser`
            : `${LAUNCH_PLATFORM_LABELS[p]}: no way to open this app`;
        return (
          <Tooltip key={p} title={title}>
            <Chip
              size="small"
              label={PLATFORM_TAGS[p]}
              color={
                native || (p === "web" && covered.has(p))
                  ? "primary"
                  : "default"
              }
              variant={native || covered.has(p) ? "filled" : "outlined"}
              sx={
                native || fallback || covered.has(p)
                  ? undefined
                  : { opacity: 0.4 }
              }
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

export default function AppCatalogPage() {
  // Mutation affordances follow the same permission the API enforces.
  const isAdmin = useCan("canManageApps");
  const [items, setItems] = useState<AppRecord[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    { mode: "create" } | { mode: "edit"; record: AppRecord } | null
  >(null);

  const refresh = useCallback(async () => {
    try {
      const list = await foyerApi.listApps();
      setItems(Array.isArray(list) ? list : []);
      setErr(null);
    } catch (e) {
      setErr(friendlyError(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onDelete(row: AppRecord) {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await foyerApi.deleteApp(row.id);
      await refresh();
    } catch (e) {
      setErr(friendlyError(e));
    }
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Catalogue"
        title="Apps"
        actions={
          isAdmin && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => setDialog({ mode: "create" })}
              data-testid="app-add"
            >
              New app
            </Button>
          )
        }
      />

      {err && (
        <Typography variant="body2" color="error">
          {err}
        </Typography>
      )}

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" stickyHeader data-testid="apps-list">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }} />
                <TableCell>Name</TableCell>
                <TableCell>Wordmark</TableCell>
                <TableCell>Tag</TableCell>
                <TableCell sx={{ width: 260 }}>Platforms</TableCell>
                {isAdmin && <TableCell sx={{ width: 96 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items?.map((row) => (
                <TableRow key={row.id} hover data-testid={`app-row-${row.id}`}>
                  <TableCell>
                    <Avatar
                      src={row.logoUrl ?? undefined}
                      variant="rounded"
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: row.color,
                        color: row.text,
                        fontSize: 14,
                      }}
                    >
                      {row.letter || row.name.charAt(0)}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.wordmark}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.tag}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <CoverageChips app={row} />
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          aria-label={`Edit ${row.name}`}
                          onClick={() =>
                            setDialog({ mode: "edit", record: row })
                          }
                          data-testid={`app-edit-${row.id}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          aria-label={`Delete ${row.name}`}
                          onClick={() => onDelete(row)}
                          data-testid={`app-delete-${row.id}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 6 : 5}>
                    <Typography variant="caption" color="text.secondary">
                      No apps yet. Click <strong>New app</strong> to add one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {dialog && (
        <AppDialog
          initial={dialog.mode === "edit" ? dialog.record : null}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await refresh();
          }}
        />
      )}
    </PageContainer>
  );
}

/** Full-width section heading inside the two-column app form grid. */
function SectionLabel({
  children,
  first = false,
}: {
  children: React.ReactNode;
  first?: boolean;
}) {
  return (
    <Box sx={{ gridColumn: { sm: "1 / -1" }, mt: first ? 0 : 1 }}>
      {!first && <Divider sx={{ mb: 1.5 }} />}
      <Typography
        variant="overline"
        sx={{ fontWeight: 600, color: "text.secondary", lineHeight: 1.6 }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function AppDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: AppRecord | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [color, setColor] = useState(initial?.color ?? "#222222");
  const [text, setText] = useState(initial?.text ?? "#ffffff");
  const [wordmark, setWordmark] = useState(initial?.wordmark ?? "");
  const [letter, setLetter] = useState(initial?.letter ?? "");
  const [logoUrl, setLogoUrl] = useState(initial?.logoUrl ?? "");
  const [tag, setTag] = useState(initial?.tag ?? "");
  const [androidSlug, setAndroidSlug] = useState(initial?.androidSlug ?? "");
  const [tizenId, setTizenId] = useState(initial?.tizenId ?? "");
  const [webosId, setWebosId] = useState(initial?.webosId ?? "");
  const [iosScheme, setIosScheme] = useState(initial?.iosScheme ?? "");
  const [webUrl, setWebUrl] = useState(initial?.webUrl ?? "");
  const [certification, setCertification] = useState(
    initial?.certification ?? ""
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Only an entry that is BOTH enrolled and already carrying its official asset
  // is locked: a freshly-enrolled one still needs its artwork set once, and
  // stranding it with no logo would be a worse failure than the one this
  // prevents. Mirrors `appCatalogStore.update`.
  const artworkLocked =
    isAppCertification(initial?.certification) && Boolean(initial?.logoUrl);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        name,
        color,
        text,
        wordmark,
        letter,
        logoUrl: logoUrl.trim() || null,
        tag,
        androidSlug: androidSlug.trim() || null,
        tizenId: tizenId.trim() || null,
        webosId: webosId.trim() || null,
        iosScheme: iosScheme.trim() || null,
        webUrl: webUrl.trim() || null,
        certification: certification || null,
      };
      if (initial) {
        await foyerApi.updateApp(initial.id, payload);
      } else {
        await foyerApi.createApp(payload);
      }
      await onSaved();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {initial ? "Edit app" : "New app"}
      </DialogTitle>
      <DialogContent>
        {/*
         * Two-column grid: the catalogue has a dozen fields (five per-platform
         * launch identifiers alone), so a single vertical stack forced a lot of
         * scrolling. Sections span the full width and act as visual anchors.
         */}
        <Box
          sx={{
            pt: 1,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            columnGap: 2,
            rowGap: 2,
            alignItems: "start",
          }}
        >
          <SectionLabel first>Identity</SectionLabel>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            autoFocus
            placeholder="Netflix"
            inputProps={{ "data-testid": "app-name" }}
          />
          <TextField
            label="Tag"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            size="small"
            placeholder="Streaming"
          />
          <TextField
            label="Wordmark"
            value={wordmark}
            onChange={(e) => setWordmark(e.target.value)}
            size="small"
            placeholder="NETFLIX"
          />
          <TextField
            label="Letter"
            value={letter}
            onChange={(e) => setLetter(e.target.value)}
            size="small"
            placeholder="N"
          />

          <SectionLabel>Appearance</SectionLabel>
          <Box>
            {artworkLocked ? (
              // NFLX-5. Shown, never offered for replacement: the brand terms
              // name a specific official asset, and this catalogue is global,
              // so one re-upload would decertify every premise showing the app
              // at once. The server refuses the write too - this is the
              // explanation, not the enforcement.
              <Stack spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Logo
                </Typography>
                {logoUrl && (
                  <Box
                    component="img"
                    src={logoUrl}
                    alt=""
                    data-testid="app-logo-locked-preview"
                    sx={{
                      maxWidth: 220,
                      maxHeight: 72,
                      objectFit: "contain",
                      bgcolor: "#fff",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                    }}
                  />
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid="app-logo-locked"
                >
                  Fixed by {CERTIFICATION_LABEL[certification as "netflix"]}{" "}
                  certification — the official artwork cannot be replaced here.
                </Typography>
              </Stack>
            ) : (
              <AssetField
                label="Logo"
                value={logoUrl}
                onChange={setLogoUrl}
                preview
                testId="app-logo"
              />
            )}
          </Box>
          <Stack direction="row" spacing={2}>
            <Stack spacing={0.5}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Background
              </Typography>
              <ColorSwatch
                value={color}
                onChange={setColor}
                testId="app-color"
                ariaLabel="Background colour"
              />
            </Stack>
            <Stack spacing={0.5}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Text
              </Typography>
              <ColorSwatch
                value={text}
                onChange={setText}
                testId="app-text"
                ariaLabel="Text colour"
              />
            </Stack>
          </Stack>

          <SectionLabel>Launch identifiers</SectionLabel>
          <TextField
            label="Android slug"
            value={androidSlug}
            onChange={(e) => setAndroidSlug(e.target.value)}
            size="small"
            placeholder="com.netflix.ninja"
            inputProps={{ "data-testid": "app-android-slug" }}
          />
          <TextField
            label="Tizen ID"
            value={tizenId}
            onChange={(e) => setTizenId(e.target.value)}
            size="small"
            placeholder="3201907018807"
            inputProps={{ "data-testid": "app-tizen-id" }}
          />
          <TextField
            label="webOS ID"
            value={webosId}
            onChange={(e) => setWebosId(e.target.value)}
            size="small"
            placeholder="netflix"
            inputProps={{ "data-testid": "app-webos-id" }}
          />
          <TextField
            label="iOS / tvOS URL scheme"
            value={iosScheme}
            onChange={(e) => setIosScheme(e.target.value)}
            size="small"
            placeholder="nflx://"
            inputProps={{ "data-testid": "app-ios-scheme" }}
          />
          <TextField
            label="Web URL"
            value={webUrl}
            onChange={(e) => setWebUrl(e.target.value)}
            size="small"
            placeholder="https://www.netflix.com/"
            helperText="Opens the installed app on a phone (App Link), and is the fallback on any screen without a native app."
            inputProps={{ "data-testid": "app-web-url" }}
            sx={{ gridColumn: { sm: "1 / -1" } }}
          />

          <SectionLabel>Certification</SectionLabel>
          <TextField
            select
            label="Certification programme"
            value={certification}
            onChange={(e) => setCertification(e.target.value)}
            size="small"
            helperText="On any premise that switches this app ON, it is pinned first in the apps grid and the Apps screen cannot be hidden. A premise that switches it off is unaffected. Leave as None for an ordinary app."
            inputProps={{ "data-testid": "app-certification" }}
            sx={{ gridColumn: { sm: "1 / -1" } }}
          >
            <MenuItem value="">None</MenuItem>
            {APP_CERTIFICATIONS.map((c) => (
              <MenuItem key={c} value={c}>
                {CERTIFICATION_LABEL[c]}
              </MenuItem>
            ))}
          </TextField>
          {err && (
            <Typography
              variant="caption"
              color="error"
              sx={{ gridColumn: { sm: "1 / -1" } }}
            >
              {err}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={busy || !name.trim()}
          data-testid="app-save"
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
